import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../../i18n/useTranslation';
import styles from './NpcDialogueModal.module.css';

// ─────────────────────────────────────────────────────────────────────────────
// NpcDialogueModal
// Pequena caixa de diálogo estilo RPG clássico exibida quando o jogador
// interage com um NPC no Mapa Hub (ex: o Ferreiro). Ao concluir a conversa,
// dispara onFinish — que o HubMap usa para abrir a mesma Janela de Forja
// (ForgeSection) utilizada no dashboard fora do HUB.
// ─────────────────────────────────────────────────────────────────────────────

interface NpcDialogueModalProps {
  /** Chave i18n do nome do NPC (ex: 'hub.npc.blacksmith.name') */
  npcNameKey: string;
  /** Ícone/emoji exibido no retrato do NPC */
  npcIcon?: string;
  /** Chaves i18n das falas, exibidas uma por vez */
  lineKeys: string[];
  /** Chamado quando o jogador conclui o diálogo (última fala) */
  onFinish: () => void;
  /** Chamado quando o jogador fecha o diálogo antes do fim */
  onClose: () => void;
  /** Texto do botão de conclusão (última fala). Padrão: "Abrir Forja". */
  finishLabel?: string;
  /** Ícone/emoji antes do texto de conclusão. Padrão: 🔨 */
  finishIcon?: string;
}

export default function NpcDialogueModal({
  npcNameKey,
  npcIcon = '🧔',
  lineKeys,
  onFinish,
  onClose,
  finishLabel,
  finishIcon = '🔨',
}: NpcDialogueModalProps) {
  const { t } = useTranslation();
  const [lineIndex, setLineIndex] = useState(0);

  const isLastLine = lineIndex >= lineKeys.length - 1;

  const handleAdvance = () => {
    if (isLastLine) {
      onFinish();
    } else {
      setLineIndex(i => i + 1);
    }
  };

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.dialogueBox}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t(npcNameKey as any)}
      >
        <button className={styles.closeBtn} onClick={onClose} aria-label="Fechar">✕</button>

        <div className={styles.portraitWrap}>
          <div className={styles.portrait}>{npcIcon}</div>
        </div>

        <div className={styles.textArea}>
          <span className={styles.npcName}>{t(npcNameKey as any)}</span>
          <p className={styles.dialogueText}>{t(lineKeys[lineIndex] as any)}</p>

          <div className={styles.actionsRow}>
            <button className={styles.skipBtn} onClick={onFinish}>
              {t('hub.npc.dialogue.skip' as any)}
            </button>
            <button className={styles.advanceBtn} onClick={handleAdvance}>
              {isLastLine
                ? `${finishIcon} ${finishLabel ?? t('hub.npc.dialogue.enterForge' as any)}`
                : `${t('hub.npc.dialogue.continue' as any)} ▸`}
            </button>
          </div>

          <div className={styles.progressDots}>
            {lineKeys.map((_, i) => (
              <span key={i} className={`${styles.dot} ${i === lineIndex ? styles.dotActive : ''}`} />
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
